/* eslint-disable @typescript-eslint/prefer-promise-reject-errors -- isApiError fails otherwise */
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  type MutationStatus,
  useMutation,
  useMutationState,
  useQuery,
  useQueryClient
} from "@tanstack/react-query";
import type {
  ClientInferRequest,
  ClientInferResponseBody
} from "@ts-rest/core";
import { useSearchParams } from "react-router-dom";

import type { Order } from "@repo/types";
import { autoId } from "~/utils/auto-id";
import { client } from "~/api/client";
import type { apiContract } from "~/api/contract";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { useInvalidateProducts } from "~/api/products/hooks/use-invalidate-products";
import { useSendWebEvent } from "~/hooks";
import { useAppContext, useDeviceData } from "~/providers/app";
import { getAuth, isProfileAuth } from "~/providers/store/auth";
import { getIsCardPaymentInProgress } from "~/providers/store/basket";
import { type MutationType } from "~/utils/tanstack-query";
import { TimeoutError, timeout } from "~/utils/timeout";
import { CARD_ORDER_RECOVERY_INTERVAL_MS } from "~/utils/recover-card-order";
import { toast } from "~/components/hooks/use-toast";
import { T } from "@repo/transifex";
import { DisplayErrorMessage } from "~/components/display-error-message";
import { DisplayError, DisplayErrorCodeEnum } from "~/utils/display-error";

type GetRequest = ClientInferRequest<typeof apiContract.v1.pos.orders.get>;

type OrdersResponse = Awaited<
  ReturnType<typeof client.v1.pos.orders.get>
>["body"];

const filterOrders = (data: OrdersResponse, searchString?: string) => {
  if (!searchString) return data;

  const searchStringLowerCased = searchString.toLowerCase();

  return {
    ...data,
    orders: data.orders.filter(
      (order) =>
        order.id.toLowerCase().includes(searchStringLowerCased) ||
        order.orderLines.some((line) =>
          line.name.toLowerCase().includes(searchStringLowerCased)
        ) ||
        order.profileName?.toLowerCase().includes(searchStringLowerCased) ||
        order.fullName?.toLowerCase().includes(searchStringLowerCased)
    )
  };
};

export const ordersKeys = createQueryKeys("orders", {
  list: ({
    tillId,
    start,
    end,
    sessionId
  }: Pick<GetRequest["query"], "tillId" | "start" | "end" | "sessionId">) => ({
    queryKey: [{ tillId, start, end, sessionId }]
  }),
  detail: (id: string) => ({ queryKey: [id] })
});

function useOrders({
  tillId,
  start,
  end,
  searchString
}: Pick<GetRequest["query"], "tillId" | "start" | "end"> & {
  searchString?: string;
}) {
  return useQuery({
    ...ordersKeys.list({ tillId, start, end }),
    queryFn: async () => {
      const res = await client.v1.pos.orders.get({
        query: {
          tillId: tillId ?? undefined,
          start,
          end
        }
      });

      if (res.status === 200) {
        return res.body;
      }

      throw res;
    },
    select: (data) => filterOrders(data, searchString),
    enabled: Boolean(tillId),
    staleTime: 1000 * 60
  });
}

function useSessionOrders({
  sessionId,
  searchString
}: {
  sessionId: GetRequest["query"]["sessionId"];
  searchString?: string;
}) {
  return useQuery({
    ...ordersKeys.list({ sessionId }),
    queryFn: async () => {
      const res = await client.v1.pos.orders.get({
        query: {
          sessionId
        }
      });

      if (res.status === 200) {
        return res.body;
      }

      throw res;
    },
    select: (data) => filterOrders(data, searchString),
    enabled: Boolean(sessionId)
  });
}

type CreateRequest = ClientInferRequest<
  typeof apiContract.v1.pos.orders.create
>;

function useOrderCreate() {
  const queryClient = useQueryClient();
  const invalidateProducts = useInvalidateProducts();

  return useMutation({
    mutationFn: async ({
      employeeUserId,
      employeeChildId,
      paidWithSaldoUnit,
      ...body
    }: Omit<
      CreateRequest["body"],
      "tillId" | "moduleId" | "schoolId" | "userId" | "childId" | "profileId"
    > & {
      employeeUserId: string | null;
      employeeChildId: string | null;
      employeeCardHex: string | null;
      paidWithSaldoUnit: number | null;
    }) => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const res = await client.v1.pos.orders.create({
        body: {
          ...body,
          tillId: auth.tillId,
          moduleId: auth.moduleId,
          schoolId: auth.schoolId,
          profileId: auth.profileId,
          childId: employeeChildId ?? auth.childId,
          userId: employeeUserId ?? auth.userId,
          paidWithSaldoUnit
        }
      });

      if (res.status === 201) {
        return res.body;
      }

      throw res;
    },
    onSuccess: (order) => {
      void queryClient.setQueryData(
        ordersKeys.detail(order.id).queryKey,
        order
      );
    },
    onError: () => {
      invalidateProducts(null);
    },
    onSettled: async (order) => {
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.list._def
      });

      if (order) {
        await queryClient.invalidateQueries({
          queryKey: ordersKeys.detail(order.id).queryKey
        });
      }
    }
  });
}

export type OrderLineWithTotal = ClientInferResponseBody<
  typeof apiContract.v1.pos.orders.getById
>["orderLines"][number];

function useOrder(id: string | null) {
  return useQuery({
    ...ordersKeys.detail(id || "empty"),
    queryFn: async () => {
      if (!id) {
        throw new Error("No order id provided");
      }

      const res = await client.v1.pos.orders.getById({
        params: { orderId: id }
      });

      if (res.status === 200) {
        return res.body;
      }

      throw res;
    },
    /** TEMPORARY-FIX: ensures we retry fetching the order (for the status) while there is an active card payment  */
    retry: (count) => {
      const isCardPaymentInProgress = getIsCardPaymentInProgress();

      if (isCardPaymentInProgress) {
        return true;
      }

      return count <= 5;
    },
    refetchInterval: ({ state: { data } }) =>
      data?.status === "pending" && getIsCardPaymentInProgress()
        ? CARD_ORDER_RECOVERY_INTERVAL_MS
        : false,
    enabled: Boolean(id)
  });
}

function useOrderCancel() {
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.v1.pos.orders.cancel({
        params: { orderId: id }
      });

      if (res.status === 204) {
        return res.body;
      }

      throw res;
    }
  });
}

function useOrderEmail() {
  return useMutation({
    mutationFn: async ({
      orderId,
      email,
      userId
    }: {
      orderId: string;
      email: string;
      userId: string | null;
    }) => {
      const res = await client.v2.pos.orders.email({
        params: { orderId },
        body: { email, employeeUserId: userId }
      });

      if (res.status === 204) {
        return res.body;
      }

      throw res;
    }
  });
}

export type OrderLinesToRefund = {
  orderLineId: string;
  productLineId?: string;
  amount: number;
  unitRefundAmountInclVat: number;
  unitRefundTaxAmount: number;
};

type OrderRefundRequest = ClientInferRequest<
  typeof apiContract.v1.pos.orders.refund
>;

type UseOrderRefundProps = {
  orderId: string;
  refundMethod: "card" | "cash" | "billing" | "swish" | "credit";
  shouldGenerateServiceId?: boolean;
} & Pick<OrderRefundRequest["body"], "refundOrderLines">;

function useOrderRefund() {
  const queryClient = useQueryClient();
  const { os, osVersion } = useDeviceData();
  const [, setSearchParams] = useSearchParams();

  return useMutation({
    mutationFn: async ({
      orderId,
      refundMethod,
      shouldGenerateServiceId = false,
      refundOrderLines
    }: UseOrderRefundProps) => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      if (!os || !osVersion) throw new Error("OS or OS Version not provided");

      let serviceId = "";
      if (refundMethod === "card" && shouldGenerateServiceId) {
        serviceId = autoId(10);
        setSearchParams({ serviceId });
      }

      const res = await client.v1.pos.orders.refund({
        params: { orderId },
        body: {
          tillId: auth.tillId,
          profileId: auth.profileId,
          refundMethod,
          deviceInfo: { os, osVersion },
          serviceId,
          refundOrderLines
        }
      });

      if (res.status === 204) {
        return res.body;
      }

      throw res;
    },
    onSuccess: async (_, { orderId }) => {
      // invalidate list queries
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.list._def
      });

      // invalidate detail query
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.detail(orderId).queryKey
      });

      toast({
        title: <T _str="Refund completed" />,
        variant: "success",
        description: <T _str="Refunded successfully" />
      });
    },
    onError: (error) => {
      toast({
        title: <T _str="Refund failed" />,
        variant: "destructive",
        description: (
          <DisplayErrorMessage
            error={error}
            fallback={<T _str="Could not refund order" />}
          />
        )
      });
    }
  });
}

function usePrintOrderReceipt() {
  const sendWebEvent = useSendWebEvent();

  const {
    hardware: { printer }
  } = useAppContext();

  return useMutation({
    mutationKey: ["create-order-receipt"],
    mutationFn: async ({
      orderId,
      userId,
      receiptList
    }: {
      orderId: string;
      isTicket?: boolean;
      userId: string | null;
      receiptList?: string[];
    }) => {
      const auth = getAuth();
      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      if (!printer) {
        throw new Error("Printer is not connected");
      }

      const res = await client.v2.pos.orders.receipt({
        params: { orderId },
        body: { employeeUserId: userId, tillId: auth.tillId }
      });

      if (res.status === 201) {
        return { ...res.body, receiptList };
      }

      throw res;
    },
    onSuccess(receipt, { isTicket }) {
      sendWebEvent({
        type: isTicket
          ? "PRINTER_PRINT_TICKET_REQUEST"
          : "PRINTER_PRINT_RECEIPT_REQUEST",
        payload: receipt
      });
    }
  });
}

const useReceiptMutationStatus = (
  orderId: string | undefined
): MutationStatus | null => {
  const mutations = useMutationState({
    filters: {
      mutationKey: ["create-order-receipt"],
      predicate: (mutation: MutationType<typeof usePrintOrderReceipt>) =>
        mutation.state.variables?.orderId === orderId
    }
  });

  if (!mutations.length) {
    return null;
  }

  const mutationState = mutations[mutations.length - 1];
  if (!mutationState) {
    return null;
  }

  return mutationState.status;
};

type CreateCardReimbursementRequest = ClientInferRequest<
  typeof apiContract.v1.pos.orders.reimbursement
>;

type UseOrderCardReimbursementCreateProps = {
  /**
   * If the timeout is reached, the transaction will be aborted.
   * @param args args needed to the abort mutation and cancel a transaction
   */
  abortTransactionCallback: (args: {
    orderId: string;
    serviceId: string;
    terminalId: string;
  }) => void;
};

function useOrderCardReimbursementCreate({
  abortTransactionCallback
}: UseOrderCardReimbursementCreateProps) {
  const queryClient = useQueryClient();
  const [, setSearchParams] = useSearchParams();

  const { data: till } = useCurrentTill();

  return useMutation({
    mutationFn: async ({
      info,
      ...body
    }: Omit<
      CreateCardReimbursementRequest["body"],
      | "tillId"
      | "moduleId"
      | "schoolId"
      | "userId"
      | "childId"
      | "profileId"
      | "serviceId"
      | "info"
    > & {
      info?: {
        textInput: Record<string, string>;
      };
    }) => {
      const auth = getAuth();

      const textInputs = info?.textInput
        ? Object.entries(info.textInput).map(([id, value]) => ({ id, value }))
        : undefined;

      if (!isProfileAuth(auth)) {
        throw new DisplayError(DisplayErrorCodeEnum.PROFILE_NOT_SELECTED);
      }

      const serviceId = autoId(10);

      // wait for the order to be created
      setTimeout(() => {
        setSearchParams({ serviceId, orderId: body.orderId });
      }, 1000);

      try {
        return await timeout(
          new Promise((resolve, reject) => {
            void client.v1.pos.orders
              .reimbursement({
                body: {
                  ...body,
                  tillId: auth.tillId,
                  moduleId: auth.moduleId,
                  schoolId: auth.schoolId,
                  profileId: auth.profileId,
                  childId: auth.childId,
                  userId: auth.userId,
                  serviceId,
                  info: textInputs ? { textInputs } : undefined
                }
              })
              .then((res) => {
                if (res.status === 201) {
                  resolve(res.body);
                } else {
                  reject(res);
                }
              });
          }),
          { ms: 300000 } // 5 minutes
        );
      } catch (error) {
        if (error instanceof TimeoutError) {
          // if the timeout (5 minutes) is reached, abort the current transaction
          till?.terminalId &&
            abortTransactionCallback({
              serviceId,
              orderId: body.orderId,
              terminalId: till.terminalId
            });
        }
        throw error;
      }
    },
    onSuccess: (_, { orderId }) => {
      void queryClient.setQueryData(
        ordersKeys.detail(orderId).queryKey,
        (old: Order) => ({
          ...old,
          status: "paid"
        })
      );
    },
    onSettled: async (_, __, { orderId }) => {
      await queryClient.invalidateQueries({
        queryKey: ordersKeys.detail(orderId).queryKey
      });
    }
  });
}

export {
  useOrder,
  useOrderCancel,
  useOrderCardReimbursementCreate,
  useOrderCreate,
  useOrderEmail,
  useOrderRefund,
  useOrders,
  usePrintOrderReceipt,
  useReceiptMutationStatus,
  useSessionOrders
};
